import { useQuery } from '@tanstack/react-query'
import { CalendarRange, MonitorPlay } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { familyRunsQuery, myRunsQuery } from '@/features/courses/api'
import { SessionList } from '@/features/courses/components/SessionList'
import { STATUS_LABEL, STATUS_TONE } from '@/features/courses/status'
import { formatRange, scheduleSummary } from '@/features/courses/format'
import type { CourseRun } from '@/features/courses/types'
import { useChild } from '@/features/lms/useChild'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

function RunCards({ runs, lmsHref }: { runs: CourseRun[]; lmsHref: (r: CourseRun) => string }) {
  const { t } = useTranslation('courses')
  if (runs.length === 0)
    return (
      <Card>
        <EmptyState
          icon={<CalendarRange />}
          title={t('No courses')}
          description={t('Scheduled courses appear here.')}
        />
      </Card>
    )
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {runs.map((r) => (
        <Card key={r.id}>
          <CardHeader
            title={r.course.name}
            description={`${r.section.label} · ${formatRange(r.startsOn, r.endsOn)}`}
            actions={<Badge tone={STATUS_TONE[r.status]}>{t(STATUS_LABEL[r.status])}</Badge>}
          />
          <CardBody className="space-y-3">
            <p className="text-sm text-ink-soft">
              {scheduleSummary(r.slots)}
              {r.teacher ? ` · ${r.teacher.name}` : ''}
            </p>
            <SessionList sessions={r.sessions ?? []} limit={4} />
            <Button size="sm" variant="secondary" leftIcon={<MonitorPlay />} asChild>
              <Link to={lmsHref(r)}>{t('Lessons and homework')}</Link>
            </Button>
          </CardBody>
        </Card>
      ))}
    </div>
  )
}

/** Staff portal: the courses I teach, their dates and next sessions. */
export function MyCoursesPage() {
  const { t } = useTranslation('courses')
  useDocumentTitle('My courses')
  const runs = useQuery(myRunsQuery())
  return (
    <>
      <PageHeader
        title={t('My courses')}
        description={t('The courses you teach, with their dates and next sessions.')}
        breadcrumbs={[{ label: t('My courses') }]}
        homeHref="/staff"
      />
      {runs.isPending ? (
        <LoadingState />
      ) : runs.error ? (
        <ErrorState onRetry={() => void runs.refetch()} />
      ) : (
        <RunCards
          runs={runs.data}
          lmsHref={(r) => `/staff/lms/courses/${r.section.id}/${r.course.subject.id}`}
        />
      )}
    </>
  )
}

/** Family / student portal: the courses the learner follows. */
export function FamilyCoursesPage() {
  const { t } = useTranslation('courses')
  useDocumentTitle('My courses')
  const { child, children } = useChild()
  const studentId = child?.student.id ?? 0
  const runs = useQuery({ ...familyRunsQuery(studentId), enabled: studentId > 0 })
  return (
    <>
      <PageHeader
        title={t('My courses')}
        description={t('Your courses, their dates and your next sessions.')}
        breadcrumbs={[{ label: t('My courses') }]}
        homeHref="/portal"
      />
      {children.isPending || (studentId > 0 && runs.isPending) ? (
        <LoadingState />
      ) : runs.error ? (
        <ErrorState onRetry={() => void runs.refetch()} />
      ) : (
        <RunCards
          runs={runs.data ?? []}
          lmsHref={(r) => `/portal/learning/${r.course.subject.id}?student=${studentId}`}
        />
      )}
    </>
  )
}
